import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { headerNavbarOptions } from 'const';
import { Avatar, Button, Dropdown } from 'components/common';
import { useEffect, useState } from 'react';
import AppRoutes from 'const/routes';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useUser } from '@/hooks';
import Icons from '@/const/icons';
import { customEvent } from '@/lib';
import Images from '@/const/images';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';

interface Props {
	className?: string;
	dataCY: string;
}

export const Navbar: React.FC<Props> = ({ className, dataCY }: Props) => {
	const logoUrl = Images.logoSVG;
	const router = useRouter();
	const { pathname } = router;
	const { user } = useUser();
	const [navbarOpen, setNavbarOpen] = useState(false);
	const [dropdownOpen, setDropdownOpen] = useState(false);

	// ------- sessions --------//
	const session = useSession();

	useEffect(() => {
		setNavbarOpen(false);
	}, [pathname]);

	return (
		<nav
			data-cy={`${dataCY}-header`}
			className={clsx(
				'flex items-center relative justify-between w-full shadow-none md:shadow-sm px-5 lg:px-[88px]',
				'4xl:px-[96px] py-3 bg-white h-[60px] md:h-24 border-0 border-transparent md:border md:border-[#0D061614] z-[15] lg:fixed',
				className
			)}
		>
			<div className="w-[163px] h-[30px] md:w-[260px] md:h-12 relative">
				<Link href="/" className="block h-12">
					<Image
						fill
						priority
						src={logoUrl}
						alt="Logo"
						quality={75}
						className="object-contain md:object-none h-12"
					/>
				</Link>
			</div>

			<div className="w-auto">
				<div
					className={clsx(
						'hidden flex-1 flex-col lg:flex lg:flex-row xl:gap-8 lg:items-center',
						'absolute lg:static left-0 bg-white top-[60px] lg:top-24 w-full items-end p-5 lg:p-0 gap-3',
						'border-b border-opacity-primary lg:border-none animate-ease-linear animate-duration-[250ms]',
						{
							'!flex animate-flip-down z-50': navbarOpen,
						}
					)}
				>
					{pathname &&
						!pathname?.startsWith('/clientapp/all-cases') &&
						headerNavbarOptions.map(({ name, label, href, onClick }) => {
							const classNameAux = `text-[16px] font-semibold flex items-center leading-[24px] cursor-pointer ${
								router.asPath === href ? `text-primary` : `text-[#1F1D21]`
							}`;
							if (onClick) {
								return (
									<button
										type="button"
										onClick={() => {
											onClick(undefined);
											href && router.push(href);
										}}
										key={name}
										className={classNameAux}
									>
										{label}
									</button>
								);
							} else {
								return (
									<Link href={href || ''} key={name} className={classNameAux}>
										{label}
									</Link>
								);
							}
						})}
					{session.status === 'authenticated' &&
					user &&
					user.role === 'client' ? (
						<Dropdown
							isOpen={dropdownOpen}
							setOpen={setDropdownOpen}
							buttonContent={
								user ? (
									<Avatar
										photoUrl={user?.profile_picture || ''}
										size="small"
										firstname={user?.firstname || ''}
										lastname={user?.lastname || ''}
									/>
								) : (
									<Avatar size="small" photoUrl={Icons.avatar} />
								)
							}
							showChevronDownIcon
							classNameButton="w-auto text-white"
							className="relative"
						/>
					) : (
						<div className="flex items-center gap-4 w-full lg:w-auto">
							<Button
								onClick={async () => {
									window.open(AppRoutes.CLIENTAPP_AUTH_SIGN_IN, '_blank');
								}}
								size="medium"
								decoration={'secondary'}
							>
								Sign In
							</Button>
							<Button
								size="medium"
								decoration={'primary'}
								className="text-nowrap"
								onClick={async () => {
									customEvent({
										action: 'sign_up_click',
										category: 'landing',
										label: 'Sign-up',
										value: 'Sign-up',
										callback: async () => {
											window.open(AppRoutes.CLIENTAPP_AUTH_SIGN_UP, '_blank');
										},
									});
								}}
							>
								Sign Up
							</Button>
						</div>
					)}
				</div>
				<div className="flex items-center lg:hidden ">
					<button
						onClick={() => {
							setNavbarOpen((prev) => !prev);
							if (navbarOpen) {
								setDropdownOpen(false);
							}
						}}
						aria-label="Menu"
					>
						{dropdownOpen ? (
							<XMarkIcon width={20} height={20} className="text-[#51526C]" />
						) : (
							<div className="flex items-center">
								<Bars3Icon width={20} height={20} className="text-[#51526C]" />
							</div>
						)}
					</button>
				</div>
			</div>
		</nav>
	);
};
